/* Klassen — Katalog/Suche: stilles Filter-/Sortier-Update ohne vollen Reload.
   Basis ohne JS: echte Links + GET-Formular in facets.liquid. Hier drauf:
   Section Rendering API (?section_id=) holt die neu gerenderte Section, wir
   tauschen Grid + Steuerleiste + Formular in-place. Kein <head>-Zugriff
   (Canonical/robots bleiben Server-Sache). Nur von collection.liquid +
   search.liquid geladen, nicht global.

   Aufbau: alle Handler nehmen den `section`-Knoten als Argument und lesen ihre
   Ziele frisch aus dem DOM -> ein Editor-Reload (shopify:section:load) tauscht
   den Section-Baum, `bindDynamic(section)` verkabelt die neuen Form-/Sort-Knoten,
   die EINE delegierte Klick-/popstate-Bindung sitzt an `document`/`window` und
   wird über ein Modul-Flag nur einmal gesetzt.
   Docs: https://shopify.dev/docs/api/ajax/section-rendering */

(function () {
"use strict";

const MotionKit = window.MotionKit;
const DEBOUNCE_MS = 500;
const controllers = new WeakMap(); // section -> laufender AbortController
let priceTimer = 0;
let docBound = false;

const activeSection = () => document.querySelector("[data-section-id]");

// Ziel-URL aus Formular- + Sortierzustand der Section bauen.
function currentUrl(section) {
  const f = section.querySelector("[data-facets-form]");
  const params = new URLSearchParams();
  if (f) {
    for (const [k, v] of new FormData(f).entries()) {
      if (v !== "" && v != null) params.append(k, v);
    }
  }
  const sort = section.querySelector("[data-facets-sort]");
  if (sort && sort.value) params.set("sort_by", sort.value);
  const base = (f && f.getAttribute("action")) || location.pathname;
  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
}

async function loadFacets(section, url, push) {
  const sectionId = section.dataset.sectionId;
  const grid = () => section.querySelector("[data-facets-grid]");
  const g = grid();
  if (g) g.setAttribute("aria-busy", "true");

  const prev = controllers.get(section);
  if (prev) prev.abort();
  const controller = new AbortController();
  controllers.set(section, controller);

  const sep = url.includes("?") ? "&" : "?";
  let html;
  try {
    const res = await fetch(`${url}${sep}section_id=${sectionId}`, { signal: controller.signal });
    if (!res.ok) throw new Error(res.status);
    html = await res.text();
  } catch (err) {
    if (err.name === "AbortError") return;
    location.assign(url); // Fallback: harter Seitenwechsel
    return;
  }

  const doc = new DOMParser().parseFromString(html, "text/html");
  ["[data-facets-grid]", "[data-facets-controls]", "[data-facets-form]"].forEach((sel) => {
    const next = doc.querySelector(sel);
    const cur = section.querySelector(sel);
    if (next && cur) cur.replaceWith(next);
  });

  if (push) history.pushState({ facets: true }, "", url);

  // Sortier-<select> + Bottom-Sheet liegen außerhalb des getauschten Bereichs
  // (.catalog__head). Der frisch gerenderte `doc` enthält die Toolbar samt
  // <select> mit korrektem `selected` für jede Ziel-URL — von dort übernehmen,
  // deckt popstate/Zurück inkl. Rücksprung auf die Default-Sortierung ab.
  const sortNext = doc.querySelector("[data-facets-sort]");
  const sortEl = section.querySelector("[data-facets-sort]");
  if (sortNext && sortEl) {
    sortEl.value = sortNext.value;
    syncSortSheet(section);
  }

  const status = section.querySelector("[data-facets-status]");
  const statusText = doc.querySelector("[data-facets-status-text]");
  if (status) {
    const ng0 = grid();
    status.textContent = statusText
      ? statusText.textContent.trim()
      : String(ng0 ? ng0.querySelectorAll("li").length : 0);
  }

  const ng = grid();
  if (ng) {
    ng.removeAttribute("aria-busy");
    // reveal(selector, { root }) scannt nur den neuen Grid-Teilbaum; NICHT init()
    // (das fasst zusätzlich scrollSetup() an).
    if (MotionKit && !MotionKit.reduced()) MotionKit.reveal("[data-reveal]", { root: ng });
  }
  bindDynamic(section);
}

// Bottom-Sheet-Optionen (außerhalb des AJAX-Bereichs) auf den <select>-Wert ziehen.
function syncSortSheet(section) {
  const sel = section.querySelector("[data-facets-sort]");
  if (!sel) return;
  section.querySelectorAll("[data-sort-option]").forEach((b) => {
    if (b.dataset.sortOption === sel.value) b.setAttribute("aria-current", "true");
    else b.removeAttribute("aria-current");
  });
}

// Form-/Sort-Handler an die (nach jedem Austausch neuen) Knoten hängen.
function bindDynamic(section) {
  const f = section.querySelector("[data-facets-form]");
  if (f && !f.dataset.bound) {
    f.dataset.bound = "1";
    f.addEventListener("submit", (e) => {
      e.preventDefault();
      loadFacets(section, currentUrl(section), true);
      // Mobil: "Übernehmen" schließt zusätzlich den Filter-Drawer und gibt den
      // Fokus zurück (drawer() aus klassen.js). Desktop: Sidebar ist nicht
      // is-open -> close() ist ein no-op. Die Schließen-Taste liegt außerhalb
      // von [data-facets-form], überlebt also den AJAX-Formulartausch.
      section.querySelector("#FacetsDrawer .filter-drawer__close")?.click();
    });
    f.addEventListener("change", (e) => {
      if (e.target.matches("[data-facets-price] input")) return; // debounce unten
      loadFacets(section, currentUrl(section), true);
    });
    f.addEventListener("input", (e) => {
      if (!e.target.matches("[data-facets-price] input")) return;
      clearTimeout(priceTimer);
      priceTimer = setTimeout(() => loadFacets(section, currentUrl(section), true), DEBOUNCE_MS);
    });
  }
  const sort = section.querySelector("[data-facets-sort]");
  if (sort && !sort.dataset.bound) {
    sort.dataset.bound = "1";
    sort.addEventListener("change", () => loadFacets(section, currentUrl(section), true));
  }
}

// Einmalig: delegierter Klick (Chips / "Alle entfernen" / "Filter zurücksetzen")
// + popstate. Beide an document/window, überstehen Section-Reloads.
function bootOnce() {
  if (docBound) return;
  docBound = true;
  document.addEventListener("click", (e) => {
    // Sortier-Sheet: Option gewählt -> <select> setzen, Grid per AJAX neu laden.
    // (Der Klick schließt das Sheet zusätzlich über drawer(), data-drawer-toggle.)
    const sortOpt = e.target.closest("[data-sort-option]");
    if (sortOpt) {
      const section = sortOpt.closest("[data-section-id]");
      if (!section) return;
      e.preventDefault();
      const sel = section.querySelector("[data-facets-sort]");
      if (sel) sel.value = sortOpt.dataset.sortOption;
      syncSortSheet(section);
      loadFacets(section, currentUrl(section), true);
      return;
    }
    const link = e.target.closest("[data-facets-active] a, .facets__clear, .product-grid__empty a");
    if (!link) return;
    const section = link.closest("[data-section-id]");
    if (!section) return;
    e.preventDefault();
    loadFacets(section, link.getAttribute("href"), true);
  });
  window.addEventListener("popstate", () => {
    const section = activeSection();
    if (section) loadFacets(section, location.pathname + location.search, false);
  });
}

function init(section) {
  const s = section || activeSection();
  if (!s) return;
  bootOnce();
  bindDynamic(s);
}

document.addEventListener("DOMContentLoaded", () => init());
document.addEventListener("shopify:section:load", (e) => {
  if (e.target && e.target.querySelector && e.target.querySelector("[data-facets-form],[data-facets-grid]")) {
    init(e.target.querySelector("[data-section-id]") || e.target);
  }
});

})();
